import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { Node } from "prosemirror-model";
import { EditorState, type Plugin } from "prosemirror-state";

import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";

import { env } from "../env";
import { $pageContent } from "./page.atom";

const logger = new Logger("page-editor");

type PageEditorPluginsFactory = (params: {
	documentId: string;
	pageId: string;
	schema: typeof schema;
	readonly: boolean;
}) => Plugin[];

let pageEditorPluginsFactory: PageEditorPluginsFactory | undefined;

export const registerPageEditorPluginsFactory = (factory: PageEditorPluginsFactory) => {
	pageEditorPluginsFactory = factory;
};

/**
 * Page editor content atom that creates a fully-initialized ProseMirror EditorState
 * Returns the actual EditorState with all plugins configured
 *
 * NOTE: This is for initial loading. Use $pageEditorState from page-editor-state.atom.ts
 * for the mutable editor state that gets updated via transactions.
 */
export const $pageInitialEditorState = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const pageLogger = new Logger("pageInitialEditorState");

		const editorStateAtom = atom(async (get): Promise<EditorState | undefined> => {
			// Don't load in SSR
			if (env.isSsr) {
				return undefined;
			}

			pageLogger.debug("Loading page initial editor state", {
				documentId,
				pageId,
			});

			const pageContent = await get($pageContent({ documentId, pageId }));

			if (!pageContent) {
				pageLogger.debug("No page content available", {
					documentId,
					pageId,
				});
				return undefined;
			}

			const readonly = !(env.isDesktopApp || env.isMobileApp);

			const plugins = pageEditorPluginsFactory
				? pageEditorPluginsFactory({
						documentId,
						pageId,
						schema,
						readonly,
					})
				: [];

			if (!pageEditorPluginsFactory) {
				logger.warn("No page editor plugins factory registered; using empty plugins list");
			}

			// Create EditorState exactly like createEditorState() does
			const doc = pageContent.content.doc;

			return EditorState.create({
				doc: Node.fromJSON(schema, doc),
				plugins,
			});
		});
		editorStateAtom.debugLabel = `pageInitialEditorState/${documentId}/${pageId}`;

		return editorStateAtom;
	},
	isEqual
);

/**
 * Raw markdown content atom for CodeMirror editor
 */
export const $pageMarkdownContent = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) =>
		atom(async (get) => {
			const pageContent = await get($pageContent({ documentId, pageId }));

			if (!pageContent) {
				return undefined;
			}

			return {
				raw: pageContent.raw ?? "",
				source: pageContent.source,
			};
		}),
	isEqual
);
// debugLabel is set on individual atom instances created by atomFamily
